// Tipos principales de la aplicación

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

export interface BriefingSession {
  sessionId: string;
  messages: ChatMessage[];
  isComplete: boolean;
  extractedData?: BriefingData;
}

export interface BriefingData {
  topic: string;
  targetKeyword: string;
  audience: string;
  tone: string;
  wordCount: number;
  additionalRequirements?: string;
}

export interface Brief {
  title: string;
  mainKeyword: string;
  secondaryKeywords: string[];
  metaTitle: string;
  metaDescription: string;
  outline: OutlineSection[];
  seoRecommendations: string[];
  targetWordCount: number;
  topicId?: string | number;
  sessionId?: string;
  // CTA (Call To Action) - opcional
  ctaText?: string;           // Texto del CTA a insertar en la conclusión
  ctaUrl?: string;            // URL del enlace del CTA
  // Título estricto vs tema
  isTitleStrict?: boolean;    // true = usar título literal, false = usar como tema (default: true)
}

export interface OutlineSection {
  heading: string;
  subheadings?: string[];
  keyPoints: string[];
  keywords: string[];
}

export interface ResearchTopic {
  topic: string;
  query: string;
  priority: 'high' | 'medium' | 'low';
}

export interface ResearchResult {
  topic: string;
  mainTopic?: string;
  synthesis: string;
  research?: string;
  sources: Source[];
  keyInsights: string[];
  error?: string;
  timestamp?: string;
  responseTime?: number;
  fullData?: any;
  model?: string;
  usage?: any;
  citations?: any[];
}

export interface Source {
  title: string;
  url: string;
  snippet: string;
  relevance?: number;
}

export interface Article {
  id?: string | number;
  session_id?: string;
  project_id?: string;
  title: string;
  content: string;
  html: string;
  html_content?: string;
  article_data?: any;
  wordCount: number;
  readingTime: number;
  metadata: {
    metaTitle: string;
    metaDescription: string;
    keywords: string[];
  };
  images?: string[];
  sources?: Array<{
    id?: number;
    title?: string;
    url?: string;
    author?: string;
    authorUrl?: string;
    year?: number;
  }>;
  schemaJsonLd?: any;
  // Tipo de artículo (wikipedia, comparative, standard, news)
  article_type?: ArticleTypeId;
  articleType?: ArticleTypeId; // Alias para compatibilidad
}

export interface ImagePrompt {
  section: string;
  prompt: string;
  style: string;
  description: string;
}

// Tipos de artículos disponibles
export type ArticleTypeId = 'wikipedia' | 'comparative' | 'standard' | 'news';

export interface ArticleTypeConfig {
  id: ArticleTypeId;
  name: string;
  description: string;
  icon: string; // nombre del icono de lucide-react
  requiresSources: boolean;
  researchLevel: 'full' | 'urls-only' | 'minimal' | 'none';
  features: string[];
}

export const ARTICLE_TYPES: ArticleTypeConfig[] = [
  {
    id: 'wikipedia',
    name: 'Enciclopédico',
    description: 'Artículo con citas numeradas [1], [2] y bibliografía al final. Ideal para contenido informativo y SEO.',
    icon: 'BookOpen',
    requiresSources: true,
    researchLevel: 'full',
    features: ['Citas numeradas', 'Fuentes verificables', 'Tono objetivo', 'Estructura Wikipedia']
  },
  {
    id: 'comparative',
    name: 'Comparativa',
    description: 'Compara productos o servicios con tabla comparativa. Tú seleccionas las URLs a analizar.',
    icon: 'GitCompare',
    requiresSources: true,
    researchLevel: 'urls-only',
    features: ['Tabla comparativa', 'Pros y contras', 'Recomendaciones', 'URLs personalizadas']
  },
  {
    id: 'standard',
    name: 'Explicativo',
    description: 'Artículo informativo sin citas. Tono cercano y profesional para explicar conceptos.',
    icon: 'FileText',
    requiresSources: false,
    researchLevel: 'minimal',
    features: ['Sin citas', 'Tono cercano', 'Ejemplos prácticos', 'Fácil lectura']
  },
  {
    id: 'news',
    name: 'Clon',
    description: 'Extrae contenido de una URL, lo reescribe en otras palabras con FAQs. Sin plagiar.',
    icon: 'Copy',
    requiresSources: true,
    researchLevel: 'minimal',
    features: ['Extracción de URL', 'Reescritura original', 'FAQs incluidas', 'Sin plagiar']
  }
];

// Configuración de taxonomías para enlaces internos
export interface TaxonomyConfig {
  slug: string;           // Slug de la taxonomía (ej: 'category', 'post_tag', 'producto')
  enabled: boolean;       // Si está habilitada para enlaces internos
  label: string;          // Label amigable (ej: 'Categorías', 'Etiquetas')
  rest_base?: string;     // Base REST API (para endpoints personalizados)
}

// Estructura flexible para enlaces internos (páginas, posts, taxonomías)
export interface LinkedContent {
  type: 'page' | 'post' | 'category' | 'tag' | string;  // Tipo de contenido (o slug de taxonomía personalizada)
  id: string | number;    // ID del contenido en WordPress
  title?: string;         // Título (opcional, para mostrar en UI)
  link?: string;          // URL del contenido (opcional)
}

export interface Project {
  id: string;
  name: string;
  description: string;
  createdAt: Date;
  updatedAt: Date;
  status: 'draft' | 'in_progress' | 'completed' | 'archived';
  thumbnail?: string;
  briefingData?: any;
  brief?: Brief;
  article?: Article;
  briefingMode?: 'chat' | 'form';
  targetKeyword?: string;
  wordCount?: number;
  researchData?: ResearchResult[];
  urlsTemas?: string[] | { id: string; url: string }[];
  wordpress_url?: string;
  wordpress_connected?: boolean | null;
  taxonomies_config?: TaxonomyConfig[];  // Configuración de taxonomías habilitadas
}

export type WorkflowStep =
  | 'briefing'
  | 'research'
  | 'structure'
  | 'generation'
  | 'review'
  | 'complete';

export interface WorkflowState {
  currentStep: WorkflowStep;
  briefingSession?: BriefingSession;
  brief?: Brief;
  researchResults?: ResearchResult[];
  article?: Article;
  imagePrompts?: ImagePrompt[];
  progress: number;
}



